/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { Locale } from "@i18n/next";
import { useSetLocale, useTranslations } from "@i18n/next";
import { useRequestMe, useSession } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import { spacing, Stack, Text } from "@packages/ui";

import { SettingsPane } from "../SettingsPane";
import LanguageOption from "./LanguageOption";

const descStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsHigher,
  marginBottom: spacing.m,
});

export const LanguageSettingsClient = () => {
  const { status } = useSession();
  const { setLocale } = useSetLocale();
  const t = useTranslations();

  const { mutationPut } = useRequestMe();

  const languages = [
    {
      key: "de",
      label: t("settings_language_german"),
    },
    {
      key: "en",
      label: t("settings_language_english"),
    },
    {
      key: "en-PH",
      label: t("settings_language_filipino"),
    },
    {
      key: "es",
      label: t("settings_language_spanish"),
    },
    {
      key: "fr",
      label: t("settings_language_french"),
    },
    {
      key: "it",
      label: t("settings_language_italian"),
    },
    {
      key: "pl",
      label: t("settings_language_polish"),
    },
    {
      key: "pt",
      label: t("settings_language_portuguese"),
    },
  ];

  const changeLanguage = async (newLocale: Locale) => {
    if (status === "authenticated")
      await mutationPut.mutateAsync({ language: newLocale });
    else setLocale({ locale: newLocale });
  };

  return (
    <SettingsPane title={t("settings_language_title")}>
      <Text size="xl" type="detail" css={descStyles}>
        {t("settings_language_description")}
      </Text>
      <Stack gap={spacing.s}>
        {languages.map((language) => (
          <LanguageOption
            key={language.key}
            value={language.key}
            label={language.label}
            onClick={() => changeLanguage(language.key as Locale)}
          />
        ))}
      </Stack>
    </SettingsPane>
  );
};
